// src/pages/AdminDashboard.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Paper,
  Typography,
  Avatar,
  CircularProgress,
  Grid,
} from "@mui/material";
import {
  AccountBalanceWalletOutlined,
  GroupsOutlined,
  PeopleOutlined,
  TrendingUp,
  CalendarMonth,
  AttachMoney,
  CheckCircle,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { getData } from "../services/request";

const currency = (value, symbol = "NGN") =>
  `${symbol} ${Number(value || 0).toLocaleString("en-US")}`;

const toGroups = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.groups)) return response.groups;
  return response?.id ? [response] : [];
};

const AdminDashboard = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // User Data
  const storedUser =
    user || JSON.parse(localStorage.getItem("userData") || "{}");
  const symbol =
    storedUser?.country?.symbol || storedUser?.country?.currency || "NGN";
  const displayName = storedUser?.fullName || storedUser?.full_name || "Admin";
  const userRole = storedUser?.userType || "groupAdmin";

  // Fetch Groups
  useEffect(() => {
    getData("/api/group/fetch_created")
      .then((response) => {
        setGroups(toGroups(response));
      })
      .catch(() => setGroups([]))
      .finally(() => setLoading(false));
  }, []);

  const goTo = (path) => {
    setMobileNavOpen(false);
    navigate(path);
  };

  const logout = () => {
    ["accessToken", "userData", "deviceId", "notifications", "nin"].forEach(
      (key) => localStorage.removeItem(key),
    );
    navigate("/", { replace: true });
  };

  // Stats
  const active = groups.filter((group) => group.isActive !== false).length;
  const members = groups.reduce(
    (total, group) =>
      total + Number(group.totalUsers || group.membersCount || 0),
    0,
  );
  const totalAmount = groups.reduce(
    (total, group) => total + Number(group.amount || 0),
    0,
  );

  return (
    <Box
      sx={{ minHeight: "100vh", backgroundColor: "#f6f7fb", color: "#1d2433" }}
    >
      {/* Navbar */}
      <Navbar
        onMenuClick={() => setMobileNavOpen(true)}
        onNavigate={goTo}
        onLogout={logout}
        user={{ fullName: displayName, userType: userRole }}
      />

      {/* Sidebar */}
      <Sidebar
        role={userRole}
        activePath={location.pathname}
        onNavigate={goTo}
        mobileOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{ ml: { xs: 0, md: "248px" }, p: { xs: 2, sm: 3, md: 5 } }}
      >
        <Box sx={{ maxWidth: 1440, mx: "auto" }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h4"
              sx={{ fontSize: { xs: 26, md: 32 }, fontWeight: 800 }}
            >
              Admin Dashboard
            </Typography>
            <Typography sx={{ color: "#7a8394", mt: 0.7 }}>
              A clear view of the savings communities you manage.
            </Typography>
          </Box>

          {/* Stats Cards */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: 2,
              mb: 4,
            }}
          >
            <StatCard
              title="Total groups created"
              value={loading ? "..." : groups.length}
              caption="Groups started by you"
              icon={<GroupsOutlined />}
              accent
            />
            <StatCard
              title="Active groups"
              value={loading ? "..." : active}
              caption="Currently accepting contributions"
              icon={<TrendingUp />}
            />
            <StatCard
              title="Total members"
              value={loading ? "..." : members}
              caption="Across your groups"
              icon={<PeopleOutlined />}
            />
            <StatCard
              title="Total savings amount"
              value={currency(totalAmount, symbol)}
              caption="Across all groups"
              icon={<AccountBalanceWalletOutlined />}
            />
          </Box>

          {/* Groups List */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          ) : groups.length ? (
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 18, mb: 2 }}>
                Your Groups
              </Typography>
              <Grid container spacing={2}>
                {groups.map((group) => (
                  <Grid item xs={12} sm={6} lg={4} key={group.id}>
                    <Paper
                      sx={{
                        p: 2.5,
                        border: "1px solid #e9ebf0",
                        borderRadius: 2,
                        boxShadow: "none",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* Group Header */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 1.5,
                        }}
                      >
                        <Avatar
                          src={group.groupImage || undefined}
                          sx={{
                            width: 48,
                            height: 48,
                            bgcolor: "#f7f1fa",
                            color: "#52057b",
                          }}
                        >
                          <GroupsOutlined />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 800, fontSize: 16 }}>
                            {group.title || "Untitled group"}
                          </Typography>
                          <Typography sx={{ color: "#7a8394", fontSize: 12 }}>
                            Created:{" "}
                            {new Date(group.createdAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={
                            group.isActive === false ? "Inactive" : "Active"
                          }
                          color={
                            group.isActive === false ? "default" : "success"
                          }
                        />
                      </Box>

                      {/* Group Details */}
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          sx={{ color: "#7a8394", fontSize: 12, mb: 1 }}
                        >
                          {group.description || "No description provided"}
                        </Typography>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 1,
                          }}
                        >
                          <Chip
                            label={`Amount: ${currency(group.amount, symbol)}`}
                            size="small"
                            sx={{ bgcolor: "#f7f1fa", color: "#52057b" }}
                          />
                          <Chip
                            label={`Fee: ${currency(group.fee, symbol)}`}
                            size="small"
                            sx={{ bgcolor: "#f7f1fa", color: "#52057b" }}
                          />

                          <Chip
                            icon={<PeopleOutlined />}
                            label={`Members: ${group.totalUsers || 1}`}
                            size="small"
                            sx={{ bgcolor: "#f7f1fa", color: "#52057b" }}
                          />
                          <Chip
                            icon={<CalendarMonth />}
                            label={`Duration: ${group.GroupPlan?.duration || "N/A"} months`}
                            size="small"
                            sx={{ bgcolor: "#f7f1fa", color: "#52057b" }}
                          />
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <Paper
              sx={{
                p: { xs: 3, sm: 5 },
                minHeight: 190,
                display: "grid",
                placeItems: "center",
                textAlign: "center",
                border: "1px solid #e9ebf0",
                borderRadius: 2,
                boxShadow: "none",
              }}
            >
              <Box>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    display: "grid",
                    placeItems: "center",
                    mx: "auto",
                    mb: 1.5,
                    borderRadius: "50%",
                    bgcolor: "#f7f1fa",
                    color: "#52057b",
                  }}
                >
                  <GroupsOutlined />
                </Box>
                <Typography sx={{ fontWeight: 800 }}>
                  No groups created yet
                </Typography>
                <Typography sx={{ color: "#7a8394", fontSize: 13, mt: 0.6 }}>
                  Create your first savings group to see its performance here.
                </Typography>
              </Box>
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Reusable Stat Card
const StatCard = ({ title, value, caption, icon, accent = false }) => (
  <Paper
    sx={{
      p: 2.5,
      minHeight: 142,
      border: "1px solid #e9ebf0",
      borderRadius: 2,
      boxShadow: "none",
      bgcolor: accent ? "#52057b" : "#fff",
      color: accent ? "#fff" : "#1d2433",
    }}
  >
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <Box>
        <Typography
          sx={{
            fontSize: 13,
            color: accent ? "rgba(255,255,255,.75)" : "#7a8394",
            fontWeight: 700,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{ fontSize: { xs: 25, sm: 29 }, fontWeight: 800, mt: 1 }}
        >
          {value}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 38,
          height: 38,
          display: "grid",
          placeItems: "center",
          borderRadius: 1.5,
          bgcolor: accent ? "rgba(255,255,255,.14)" : "#f7f1fa",
          color: accent ? "#fff" : "#52057b",
        }}
      >
        {icon}
      </Box>
    </Box>
    <Typography
      sx={{
        color: accent ? "rgba(255,255,255,.7)" : "#7a8394",
        fontSize: 12,
        mt: 2,
      }}
    >
      {caption}
    </Typography>
  </Paper>
);

export default AdminDashboard;
